import { useMemo } from 'react'

import ReactEChartsCore from 'echarts-for-react/lib/core'       // Shrink bundle size!!!
import * as echarts from 'echarts/core'
import { SunburstChart, type SunburstSeriesOption } from 'echarts/charts'
import { TooltipComponent, type TooltipComponentOption } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'

import type { ComposeOption } from 'echarts/core'
import { ROOT_CATEGORY_NAME, type CategoryTreeNode, type TickerWithValue } from '../../hooks/useCategoriesTree.ts'
import styles from './CategoriesSunburstChart.module.css'

echarts.use([SunburstChart, TooltipComponent, CanvasRenderer])  // Shrink bundle size!!!

type CategoriesSunburstOption = ComposeOption<SunburstSeriesOption | TooltipComponentOption>

type SunburstNode = {
  name: string
  value?: number
  children?: SunburstNode[]
  itemStyle?: {
    color?: string
  }
  tooltipValueLabel?: string
}

type CategoriesSunburstChartProps = {
  // O chart recebe apenas o modelo derivado pronto para apresentação.
  tree: CategoryTreeNode[]
  tickersByCategory: Map<number, TickerWithValue[]>
  leafDepth?: number
}

function formatCurrency(value?: number | null) {
  if (value === null || value === undefined || Number.isNaN(value)) return '—'

  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function mapTickersToSunburst(tickers: TickerWithValue[]): SunburstNode[] {
  return tickers.map((ticker) => ({
    name: ticker.ticker,
    value: ticker.value ?? 0,
    tooltipValueLabel: formatCurrency(ticker.value),
  }))
}

function mapCategoryTreeToSunburst(
  nodes: CategoryTreeNode[],
  tickersByCategory: Map<number, TickerWithValue[]>,
  leafDepth: number,
  currentDepth = 0,
): SunburstNode[] {
  return nodes.map((node) => {
    const canExpandChildren = currentDepth < leafDepth
    const childCategories = canExpandChildren
      ? mapCategoryTreeToSunburst(node.children, tickersByCategory, leafDepth, currentDepth + 1)
      : []
    const childTickers = canExpandChildren ? mapTickersToSunburst(tickersByCategory.get(node.id) ?? []) : []
    const children = [...childCategories, ...childTickers]

    return {
      name: node.name,
      value: node.totalValue ?? 0,
      tooltipValueLabel: formatCurrency(node.totalValue),
      children: children.length > 0 ? children : undefined,
    }
  })
}

export function CategoriesSunburstChart({
  tree,
  tickersByCategory,
  leafDepth = 3,
}: CategoriesSunburstChartProps) {
  const data = useMemo(
    () => mapCategoryTreeToSunburst(tree, tickersByCategory, leafDepth),
    [tree, tickersByCategory, leafDepth],
  )

  const option = useMemo<CategoriesSunburstOption>(
    () => ({
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => {
          const treePathInfo = 'treePathInfo' in params && Array.isArray(params.treePathInfo)
            ? params.treePathInfo
            : []
          const path = treePathInfo
            .map((item: { name?: string }) => item.name)
            .filter((name: string | undefined): name is string => Boolean(name) && name !== ROOT_CATEGORY_NAME)
            .join(' / ')

          const nodeData =
            typeof params.data === 'object' && params.data ? (params.data as SunburstNode) : undefined
          const rawValue =
            typeof params.value === 'number'
              ? params.value
              : Array.isArray(params.value) && typeof params.value[0] === 'number'
                ? params.value[0]
                : null
          const valueLabel = nodeData?.tooltipValueLabel ?? formatCurrency(rawValue)

          return [
            `<div class="${styles.tooltip}">`,
            `<div><strong>${params.name}</strong></div>`,
            path ? `<div>${path}</div>` : '',
            `<div>Valor: ${valueLabel}</div>`,
            '</div>',
          ].join('')
        },
      },
      series: [
        {
          type: 'sunburst',
          data,
          //radius: ['12%', '92%'],   // raio interno e externo dentro da área disponível do chart
          radius: ['10%', '98%'],
          sort: undefined,
          nodeClick: 'rootToNode',
          emphasis: {
            focus: 'ancestor',
          },
          label: {
            rotate: 'radial',
          },
          itemStyle: {
            borderWidth: 2,
            borderColor: '#fff',
          },
        },
      ],
    }),
    [data],
  )

  return (
    <div className={styles.chartShell}>
      <ReactEChartsCore
        echarts={echarts}
        option={option}
        notMerge
        lazyUpdate
        className={styles.chart}
        //style={{ width: '100%', height: 'min(100vw, 100vh)' }}
      />
    </div>
  )
}
